import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useProducts,
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import ProductScroller from '../../product-scroller'
import {useIntl} from 'react-intl'
import {useGlobaleContext} from '../../../contexts/globale'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS, GWP_PROMOTION_COOKIE_ID} from '../../../constants'
import {useLocation} from 'react-router-dom'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import ProductViewModal from '../../product-view-modal'
import {useAddToCartDrawerContext} from '../../../hooks/use-add-to-cart-drawer'
import useTagManager from '../../../hooks/use-tag-manager'
import useMonetate from '../../../hooks/use-monetate'
import {useToast} from '../../../hooks/use-toast'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {getItemBadge, getPriceData} from '../../../utils/product-utils'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useSiteContext} from '../../../contexts/site'
import {useWishList} from '../../../hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import LoadingSpinner from '../../loading-spinner'
import {scheduleIdleAnalytics} from '../../../utils/common-utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../../utils/log-basket-error-to-ssr'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'

export const CompleteTheLook = ({...rest}) => {
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const variantId = rest.variantId
    const completeTheLookIDs = rest.completeTheLookIDs
    const completeTheLookMessage = rest.completeTheLookMessage
    const productIds = completeTheLookIDs?.find((o) => o?.[variantId])?.[variantId] || []
    const intl = useIntl()
    const spaceBetween = {base: 0}
    const {countryISO, currencyCode} = useGlobaleContext()
    // ─── Analytics ───────────────────────────────────────────────────────────
    const tagManager = useTagManager()
    const monetate = useMonetate()

    const analyticsProps = {
        component_id: 'complete-the-look',
        component_name: 'complete-the-look'
    }

    // ─── Commerce API ────────────────────────────────────────────────────────
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    // ─── Routing ─────────────────────────────────────────────────────────────
    const location = useLocation()

    // ─── Site & Globale Context ──────────────────────────────────────────────
    const {setOpenBonusModal} = useSiteContext()

    // ─── Customer & Wishlist ─────────────────────────────────────────────────
    const {data: wishlist} = useWishList()
    const {data: customer} = useCurrentCustomer()

    const [tileProduct, setTileProduct] = useState()
    const [showSpinner, setShowSpinner] = useState(false)
    const completeTheLookRef = useRef(null)
    const isNearViewport = useIntersectionObserver(completeTheLookRef, {
        useOnce: true,
        rootMargin: '600px 0px'
    })
    const enabledCtl = Boolean(productIds.length) && !isServer && isNearViewport
    const {
        data: products,
        isLoading,
        isError,
        isSuccess
    } = useProducts(
        {
            parameters: {
                ids: productIds,
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                c_selectedCountry: countryISO,
                select: selectedAttributes,
                c_selectedCurrency: currencyCode,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true
            }
        },
        {
            enabled: enabledCtl,
            keepPreviousData: true
        }
    )

    const updatedProducts = products?.data?.filter((p) => p?.inventory?.orderable)
    const isEmpty = isError || (isSuccess && !updatedProducts?.length)

    const fetchProduct = async (productId) => {
        setShowSpinner(true)
        const token = await getTokenWhenReady()

        const requestParams = {
            id: productId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'page_meta_tags',
                'bundled_products'
            ],
            allImages: true,
            c_selectedCountry: countryISO,
            c_selectedCurrency: currencyCode
        }
        try {
            const product = await api.shopperProducts.getProduct({
                parameters: requestParams,
                headers: {
                    Authorization: `Bearer ${token}`
                },
                keepPreviousData: true
            })
            onQuickAddModalOpen(true)
            // TODO What to happen if we have one SKU only.
            setTileProduct(product)
            setShowSpinner(false)
        } catch (e) {
            setShowSpinner(false)
            console.error('Error occurred in fetching and opening a modal', e.message)
        }
    }

    useEffect(() => {
        const handleClick = (event) => {
            const link = event.target.closest('.mt-ctl-quick-add-link')

            if (link) {
                event.preventDefault()

                const productId = link.dataset.productId
                fetchProduct(productId)
            }
        }

        document.addEventListener('mousedown', handleClick)

        return () => {
            document.removeEventListener('mousedown', handleClick)
        }
    }, [])

    // ─── Messaging ────────────────────────────────────────────────────────────────
    const {formatMessage} = useIntl()

    // ─── Toast ───────────────────────────────────────────────────────────────
    const showToast = useToast()

    const showError = (message, statusCode) => {
        showToast({
            title: statusCode === '403' ? message : formatMessage(message),
            status: 'error'
        })
    }

    // ─── Drawer & Modal ──────────────────────────────────────────────────────
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()

    const {
        isOpen: isQuickAddModalOpen,
        onOpen: onQuickAddModalOpen,
        onClose: onQuickAddModalClose
    } = useDisclosure()

    // ─── Effects ─────────────────────────────────────────────────────────────

    // Close the add-to-cart drawer on route change
    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location.pathname])

    // ─── Render ──────────────────────────────────────────────────────────────

    /**
     * Handles all side-effects after a successful add-to-cart action:
     * - GWP promotion cookie check & bonus modal trigger
     * - Add-to-cart drawer open (when no GWP promo active)
     * - Monetate page tracking
     * - Tag Manager add-to-cart event
     */
    const addToBagActions = async (variant) => {
        try {
            const itemsAdded = await addItemToNewOrExistingBasket([
                {
                    productId: variant.productId,
                    price: variant.price,
                    quantity: 1,
                    c_analyticsAttributes: JSON.stringify({
                        listID: 'complete-the-look',
                        listName: 'complete-the-look',
                        itemBadge: getItemBadge(variant, {selectedVariant: variant})
                    })
                }
            ])

            if (itemsAdded) {
                // Prioritise GWP promotions as per business requirement
                const promoId = (
                    itemsAdded?.bonusDiscountLineItems?.find((i) => /gwp/i?.test(i.promotionId)) ||
                    itemsAdded?.bonusDiscountLineItems?.[0]
                )?.promotionId

                const cookie =
                    onClient() &&
                    new CookieStorage({
                        keySuffix: '',
                        sharedContext: !onClient()
                    })

                const gwpPromotionState =
                    onClient() && cookie ? cookie.get(GWP_PROMOTION_COOKIE_ID) : null
                const selectedSku = {
                    ...tileProduct,
                    ...variant
                }

                const priceData = getPriceData(selectedSku)

                // Only open the drawer when there is no active GWP promotion awaiting acknowledgement
                if (!(promoId && gwpPromotionState !== 'closed')) {
                    const selectedImage = document
                        .querySelector(
                            "div[data-testid='product-view-modal'] .img-wrapper .cloudinary-image"
                        )
                        ?.getAttribute('src')
                    onAddToCartDrawerOpen({
                        product: selectedSku,
                        itemsAdded,
                        selectedQuantity: 1,
                        priceData,
                        ...(selectedImage && {heroImage: selectedImage})
                    })
                }

                setOpenBonusModal(true)

                scheduleIdleAnalytics(() => {
                    if (itemsAdded) {
                        monetate.monetateTrackPage(
                            'addToBagModal',
                            window.mtBreadcrumb,
                            itemsAdded?.productItems,
                            customer?.customerType === 'registered',
                            wishlist?.customerProductListItems?.length || 0,
                            null,
                            null,
                            [
                                {
                                    masterId:
                                        (tileProduct?.hitType === 'master'
                                            ? tileProduct?.productId
                                            : '') ||
                                        tileProduct?.master?.masterId ||
                                        tileProduct?.representedProduct?.c_masterVariantID ||
                                        tileProduct?.id,
                                    variantId: variant?.type?.master
                                        ? variant?.variants?.[0]?.productId
                                        : variant?.productId || variant?.id
                                }
                            ],
                            itemsAdded?.giftCertificateItems
                        )
                    }
                    tagManager.addToCart(
                        tileProduct,
                        tileProduct,
                        null,
                        null,
                        'recommender',
                        null,
                        false,
                        {},
                        false,
                        false,
                        false,
                        analyticsProps
                    )
                })
            }
            setTimeout(() => {
                onQuickAddModalClose()
            }, 3000)
        } catch (error) {
            const errResponse = error?.response ? await error?.response?.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'CompleteTheLook.addToBagActions',
                additionalProperties: {error, errResponse, productId: variant?.productId}
            })
            logBasketErrorToSsr('CompleteTheLook.addToBagActions', {
                error,
                errResponse,
                productId: variant?.productId
            })

            if (errResponse?.statusCode === '403') {
                showError(errResponse.statusMessage, errResponse.statusCode)
            } else {
                showError(API_ERROR_MESSAGE)
            }

            setShowSpinner(false)
            throw error
        }
    }

    if (productIds.length === 0) {
        return null
    }

    return (
        <Stack
            ref={completeTheLookRef}
            spacing="20px"
            className={`complete-the-look ${isEmpty ? 'hide-element' : ''}`}
            id="pdp-complete-the-look"
            data-loading={!isNearViewport || isLoading || !products}
            margin={{base: '0 0 30px 0', lg: '0px 0px 30px 0'}}
            minHeight="350px"
            maxWidth="calc(100vw - var(--scrollbar-width) - 15px)"
            width="100%"
            sx={{
                '& .tile-wrapper[data-is-not-orderable="true"]': {
                    display: 'none'
                },
                '& .image-wrapper': {
                    backgroundColor: 'gray.100',
                    img: {
                        mixBlendMode: 'multiply'
                    }
                },
                '.swiper-button-prev, .swiper-button-next': {
                    top: '-10px !important',
                    opacity: '1 !important',
                    display: 'block !important',
                    left: 'initial !important',
                    boxShadow: 'none !important',
                    backgroundColor: 'transparent !important',
                    '&:hover': {
                        backgroundColor: 'transparent !important'
                    },
                    '&.swiper-button-disabled': {
                        opacity: '0.35 !important',
                        display: 'block !important'
                    }
                },
                'b.current-price': {
                    color: 'black.100 !important'
                },
                '.swiper-button-next': {
                    right: '-10px !important'
                },
                '.swiper-button-prev': {
                    right: '37px !important'
                },
                '.list-title': {
                    fontSize: '23px',
                    fontWeight: '500',
                    marginBottom: '10px',
                    fontFamily: 'caslon',
                    color: 'black.100',
                    padding: 0,
                    lineHeight: 'normal',
                    letterSpacing: '0.115px'
                },
                '.spinner img': {
                    width: '45px',
                    height: '45px'
                },
                'div[data-testid="product-scroller"]': {
                    paddingY: '0px'
                },
                img: {
                    width: '152px',
                    minWidth: '152px',
                    height: '190px'
                },
                '.swiper-slide': {
                    width: '152px !important',
                    marginRight: '20px !important',

                    '&.swiper-slide:last-child': {
                        marginRight: '20px !important'
                    }
                },
                'div[role="radiogroup"]': {
                    display: 'none'
                },
                '.info-wrapper': {
                    margin: '6.58px 0 0 0'
                },
                '.display-price-wrapper': {
                    marginRight: '6.58px',
                    marginBottom: '0'
                },
                '.product-name': {
                    margin: '0px 6.58px 6.58px 0',
                    fontSize: '18px',
                    minHeight: '45px',
                    letterSpacing: '0.075px'
                },
                '.info-wrapper b': {
                    fontSize: '14px',
                    letterSpacing: '0.065px'
                },
                '.swiper-wrapper': {
                    paddingLeft: '20px !important',
                    gap: '0'
                },
                s: {
                    fontSize: '14px'
                },
                '.swiper': {
                    padding: '0 20px 0 0 !important',
                    margin: '0 !important',
                    position: 'static'
                },
                '.swiper-parent': {
                    width: '100%',
                    display: 'grid',
                    border: '1px solid',
                    borderRadius: '10px',
                    paddingX: '0',
                    paddingY: '20px',
                    borderColor: 'gray.400',
                    backgroundColor: 'white'
                },
                '.tiles-skeleton-wrapper': {
                    marginLeft: '0px',
                    gap: '20px',
                    marginTop: '0px',
                    minHeight: '290px'
                },
                '.skeleton-box, .skeleton-tile-image': {
                    width: '152px !important'
                },
                '.skeleton-tile-image': {
                    height: '190px !important'
                },
                '.wishlist-wrapper': {
                    display: 'none !important'
                },
                '.skeleton-tile-name': {
                    width: '100px',
                    height: '22px'
                },
                '.skeleton-tile-price': {
                    height: '20px'
                },
                '.title-skeleton': {
                    margin: '0 0 15px 0',
                    height: '30px',
                    width: '205px'
                },
                '.swiper .swiper-button-next:hover, .swiper .swiper-button-prev:hover': {
                    backgrounColor: 'gray.50'
                }
            }}
        >
            <ProductScroller
                products={updatedProducts}
                hideArrows={updatedProducts?.length < 3}
                itemWidth={{xl: '199px', lg: '174px', md: '110px', '2xl': '274px', base: '263px'}}
                hideVariations={true}
                isCompleteTheLook={true}
                showImageSlider={true}
                isplp={false}
                draggable={true}
                isLoading={!isNearViewport || isLoading}
                showQuickAdd={false}
                title={
                    completeTheLookMessage ||
                    intl.formatMessage({
                        id: 'complete.the.look',
                        defaultMessage: 'Complete the Look'
                    })
                }
                showTitle={true}
                spaceBetween={spaceBetween}
                loop={true}
                grabCursor={true}
                freeMode={true}
                mousewheel={true}
            />
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {isQuickAddModalOpen && (
                <ProductViewModal
                    isOpen={isQuickAddModalOpen}
                    onOpen={onQuickAddModalOpen}
                    onClose={onQuickAddModalClose}
                    isQuickAddModal={true}
                    product={tileProduct}
                    showDeliveryOptions={false}
                    updateCart={async (variant) => {
                        setShowSpinner(true)
                        await addToBagActions(variant)
                        setShowSpinner(false)
                    }}
                />
            )}
        </Stack>
    )
}

CompleteTheLook.propTypes = {
    completeTheLookMessage: PropTypes.string,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    setProducts: PropTypes.any,
    titleTag: PropTypes.string
}

export default CompleteTheLook
